import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator, Plus } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

interface Particle {
  mass: string;
  x: string;
  y: string;
}

export default function CenterMassCalculator() {
  const [particles, setParticles] = useState<Particle[]>([
    { mass: "", x: "", y: "" },
    { mass: "", x: "", y: "" },
  ]);
  const [result, setResult] = useState<{ x: number; y: number } | null>(null);

  const addParticle = () => {
    setParticles([...particles, { mass: "", x: "", y: "" }]);
  };

  const updateParticle = (index: number, field: keyof Particle, value: string) => {
    const newParticles = [...particles];
    newParticles[index][field] = value;
    setParticles(newParticles);
  };

  const removeParticle = (index: number) => {
    if (particles.length > 2) {
      setParticles(particles.filter((_, i) => i !== index));
    }
  };

  const calculateCenterOfMass = () => {
    const validParticles = particles
      .map(p => ({
        mass: parseFloat(p.mass),
        x: parseFloat(p.x),
        y: parseFloat(p.y),
      }))
      .filter(p => !isNaN(p.mass) && !isNaN(p.x) && !isNaN(p.y));

    if (validParticles.length < 2) {
      toast.error("Please enter at least 2 valid particles");
      return;
    }

    const totalMass = validParticles.reduce((sum, p) => sum + p.mass, 0);
    const xCM = validParticles.reduce((sum, p) => sum + p.mass * p.x, 0) / totalMass;
    const yCM = validParticles.reduce((sum, p) => sum + p.mass * p.y, 0) / totalMass;

    setResult({ x: xCM, y: yCM });
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setParticles([
      { mass: "", x: "", y: "" },
      { mass: "", x: "", y: "" },
    ]);
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Center of Mass Calculator" calculatorPath="/calculator/center-mass">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Center of Mass Calculator</h1>
          <p className="text-muted-foreground">
            Find the center of mass for a system of particles
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: r_cm = Σ(m_i × r_i) / Σm_i</CardTitle>
            <CardDescription>
              Where r_cm is center of mass position, m_i is mass, r_i is position
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              {particles.map((particle, index) => (
                <Card key={index} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium">Particle {index + 1}</h3>
                    {particles.length > 2 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeParticle(index)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Mass (kg)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="m"
                        value={particle.mass}
                        onChange={(e) => updateParticle(index, "mass", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>X position (m)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="x"
                        value={particle.x}
                        onChange={(e) => updateParticle(index, "x", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Y position (m)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="y"
                        value={particle.y}
                        onChange={(e) => updateParticle(index, "y", e.target.value)}
                      />
                    </div>
                  </div>
                </Card>
              ))}

              <Button onClick={addParticle} variant="outline" className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                Add Particle
              </Button>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateCenterOfMass} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Center of Mass</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-2xl font-bold text-primary">
                        ({result.x.toFixed(3)}, {result.y.toFixed(3)}) m
                      </p>
                      <p className="text-sm text-muted-foreground">
                        X: {result.x.toFixed(3)} m, Y: {result.y.toFixed(3)} m
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["center-mass"]} trigger={result.x} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter mass and position (x, y) for each particle</p>
                <p>2. Add more particles using the "Add Particle" button</p>
                <p>3. Click Calculate to find the center of mass</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: Two 1 kg masses at (0,0) and (2,0) → CM at (1,0)
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
